// MA-941 (D-TUX-30) — email sign-in by a code typed in the same tab.
//
// A sign-in LINK fails for school families: under the PKCE flow
// (src/supabaseClient.js) a link opened in another browser or app cannot
// finish, a school mail scanner that opens it first spends it, and the
// pending claim code (src/lib/teacher/pendingClaimCode.ts) lives in this
// tab's sessionStorage. A typed code never leaves the tab.
//
// The send side is unchanged (signInWithEmailOtp in src/supabaseClient.js,
// not edited); whether the email carries a code or a link is the Supabase
// email template, a founder setting (docs/ops/FOUNDER-FYI.md, MA-289 entry).
// This module is the verify side only. It takes the auth client as an
// argument so tests can drive it with a stub; the screen passes the app's
// one Supabase client's `auth`.

/** Supabase's email OTP length is a project setting (6 to 10 digits;
 *  FOUNDER-FYI asks for 8). The field accepts any length in that range. */
export const EMAIL_CODE_MIN_DIGITS = 6;
export const EMAIL_CODE_MAX_DIGITS = 10;

/** Shown when verifyOtp fails for any reason (wrong, expired, or spent code). */
export const EMAIL_CODE_ERROR_TEXT =
  "That code didn't work. Check the newest email, or send a new code.";

/** Supabase refuses a second email to the same address inside 60 seconds,
 *  so "Send a new code" appears only after this long. */
export const EMAIL_CODE_RESEND_AFTER_MS = 60_000;

/** The last code sent from this screen: the address (as typed) and when. */
export type LastCodeSend = { email: string; at: number } | null;

/** Same address for the resend wait: trimmed and lowercased. */
export function sameCodeAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Milliseconds left in the 60-second wait for `email`: > 0 when a code was
 * sent to the same address less than 60 seconds ago (so Send must show the
 * code step again WITHOUT sending), else 0.
 */
export function resendWaitLeftMs(last: LastCodeSend, email: string, now: number): number {
  if (!last || !sameCodeAddress(last.email, email)) return 0;
  return Math.max(0, last.at + EMAIL_CODE_RESEND_AFTER_MS - now);
}

/** Keep only digits, at most EMAIL_CODE_MAX_DIGITS of them (what the field shows). */
export function emailCodeDigits(raw: string): string {
  return String(raw ?? "").replace(/\D/g, "").slice(0, EMAIL_CODE_MAX_DIGITS);
}

/** True iff `code` is 6 to 10 digits and nothing else. */
export function isCompleteEmailCode(code: string): boolean {
  return /^\d{6,10}$/.test(code);
}

export type VerifyOtpAuth = {
  verifyOtp(params: { email: string; token: string; type: "email" }): Promise<{
    data: unknown;
    error: unknown;
  }>;
};

/**
 * Sign in with the code from the email. Calls `auth.verifyOtp` exactly once
 * with `{ email, token, type: "email" }`; on success the client stores the
 * session itself (and the app's auth listener leaves the login screen).
 * Throws when the code is not 6 to 10 digits (no request is made) or when
 * the auth server refuses it.
 */
export async function verifyEmailCode(
  auth: VerifyOtpAuth,
  email: string,
  code: string,
): Promise<unknown> {
  if (!isCompleteEmailCode(code)) {
    throw new Error(`A sign-in code is ${EMAIL_CODE_MIN_DIGITS} to ${EMAIL_CODE_MAX_DIGITS} digits.`);
  }
  const { data, error } = await auth.verifyOtp({ email, token: code, type: "email" });
  if (error) throw error;
  return data;
}
